/**
 * HTTP client and every API call the app makes.
 *
 * FastAPI reports failures as `{ detail: string | ValidationError[] }`; both
 * shapes become one readable `ApiError.message`.
 */

import type {
  Company,
  CompanyCategory,
  CompanyFilters,
  CompanyInput,
  Discovery,
  ImportResult,
  Insights,
  Job,
  JobDetail,
  JobFacets,
  JobFilters,
  Market,
  Page,
  Preview,
  Queue,
  Run,
  RunStatus,
  Source,
  Stats,
} from './types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

/** Fired when the API answers 401: the session cookie expired or is missing. */
export const SIGNED_OUT_EVENT = 'scheduler:signed-out'

export interface Session {
  authenticated: boolean
  username: string | null
  login_required: boolean
}

export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export function buildQuery(query?: object): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '' || value === false) continue
    params.append(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json()
    const detail = body?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && detail.length) {
      return detail
        .map((d: { loc?: unknown[]; msg?: string }) => {
          const field = Array.isArray(d.loc) ? d.loc.filter((p) => p !== 'body').join('.') : ''
          return field ? `${field}: ${d.msg}` : d.msg
        })
        .join('; ')
    }
  } catch {
    // not JSON
  }
  return response.status >= 500
    ? 'The server hit an error. Try again in a moment.'
    : `Request failed (${response.status})`
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: object; form?: FormData } = {},
): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE_URL}${path}${buildQuery(options.query)}`, {
      method,
      headers: options.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: options.form ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    })
  } catch {
    throw new ApiError('Cannot reach the API. Is the backend running on port 8100?', 0)
  }
  if (response.status === 401 && !path.startsWith('/auth/')) window.dispatchEvent(new Event(SIGNED_OUT_EVENT))
  if (!response.ok) throw new ApiError(await readError(response), response.status)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  // ---- Sign-in
  session: () => request<Session>('GET', '/auth/session'),
  login: (username: string, password: string) => request<Session>('POST', '/auth/login', { body: { username, password } }),
  logout: () => request<void>('POST', '/auth/logout'),

  stats: () => request<Stats>('GET', '/stats'),
  insights: (market?: Market) => request<Insights>('GET', '/insights', { query: { market } }),

  // ---- Companies
  companies: (filters: CompanyFilters) => request<Page<Company>>('GET', '/companies', { query: filters }),
  company: (id: number) => request<Company>('GET', `/companies/${id}`),
  industries: () => request<string[]>('GET', '/companies/industries'),
  createCompany: (body: CompanyInput) => request<Company>('POST', '/companies', { body }),
  updateCompany: (id: number, body: Partial<CompanyInput>) => request<Company>('PATCH', `/companies/${id}`, { body }),
  deleteCompany: (id: number) => request<void>('DELETE', `/companies/${id}`),
  checkCompany: (id: number, full = false) => request<Company>('POST', `/companies/${id}/check`, { body: { full } }),
  checkMany: (body: { company_ids?: number[]; market?: Market; category?: CompanyCategory; tier?: number; full?: boolean }) =>
    request<{ companies: number; career_pages_queued: number }>('POST', '/companies/check', { body }),
  importCompanies: (file: File, defaultMarket: Market | '', defaultCategory: CompanyCategory) => {
    const form = new FormData()
    form.append('file', file)
    if (defaultMarket) form.append('default_market', defaultMarket)
    form.append('default_category', defaultCategory)
    return request<ImportResult>('POST', '/companies/import', { form })
  },
  discover: (website: string) => request<Discovery>('POST', '/companies/discover', { body: { website } }),
  careerPages: (companyId: number) => request<Source[]>('GET', `/companies/${companyId}/career-pages`),
  addCareerPage: (companyId: number, url: string) =>
    request<Source>('POST', `/companies/${companyId}/career-pages`, { body: { url } }),
  companyRuns: (companyId: number) => request<Run[]>('GET', `/companies/${companyId}/runs`),

  // ---- Career pages
  preview: (url: string) => request<Preview>('POST', '/career-pages/preview', { body: { url } }),
  checkPage: (id: number, full = false) => request<Source>('POST', `/career-pages/${id}/check`, { query: { full } }),
  deletePage: (id: number) => request<void>('DELETE', `/career-pages/${id}`),
  queue: () => request<Queue>('GET', '/scheduler/queue'),

  // ---- Jobs
  jobs: (filters: JobFilters) => request<Page<Job>>('GET', '/jobs', { query: filters }),
  job: (id: number) => request<JobDetail>('GET', `/jobs/${id}`),
  jobFacets: (filters: JobFilters) => {
    const { page: _p, page_size: _s, sort: _o, ...rest } = filters
    return request<JobFacets>('GET', '/jobs/facets', { query: rest })
  },
  exportUrl: (filters: JobFilters) => {
    const { page: _p, page_size: _s, sort: _o, ...rest } = filters
    return `${BASE_URL}/jobs/export${buildQuery(rest)}`
  },

  runs: (query: { status?: RunStatus | ''; company_id?: number; new_only?: boolean; page?: number; page_size?: number }) =>
    request<Page<Run>>('GET', '/runs', { query }),
}
